import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "emb_admin_key";

const AdminAuthContext = createContext({
  adminKey: null,
  isAuthenticated: false,
  login: async () => false,
  logout: () => {},
  authedAxios: axios,
});

/**
 * Light-weight admin auth using the same ADMIN_API_KEY that protects
 * POST/PUT/DELETE /api/posts. The key is verified against the backend
 * by attempting an authenticated request (DELETE on a non-existent slug
 * returns 204 with valid key, 401 without).
 *
 * Stored in sessionStorage so it clears when the tab closes — never in
 * localStorage, which would persist across browser restarts.
 */
export const AdminAuthProvider = ({ children }) => {
  const [adminKey, setAdminKey] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const login = useCallback(async (key) => {
    if (!key) return false;
    try {
      // Probe: DELETE a definitely-non-existent slug. The backend will
      // 401 if key is wrong, 204 if valid (because delete_one on missing
      // doc is a no-op).
      const probeSlug = `__probe-${Date.now()}__`;
      const res = await axios.delete(`${API}/posts/${probeSlug}`, {
        headers: { "X-Admin-Key": key },
        validateStatus: () => true,
      });
      if (res.status === 204) {
        sessionStorage.setItem(STORAGE_KEY, key);
        setAdminKey(key);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAdminKey(null);
  }, []);

  // Pre-configured axios for authenticated calls
  const authedAxios = axios.create();
  authedAxios.interceptors.request.use((config) => {
    if (adminKey) {
      config.headers["X-Admin-Key"] = adminKey;
    }
    return config;
  });
  // Auto-logout on 401
  authedAxios.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) logout();
      return Promise.reject(err);
    }
  );

  // If a key is stored, verify it's still valid on mount (silent re-check)
  useEffect(() => {
    if (!adminKey) return;
    const verify = async () => {
      const probeSlug = `__probe-${Date.now()}__`;
      const res = await axios.delete(`${API}/posts/${probeSlug}`, {
        headers: { "X-Admin-Key": adminKey },
        validateStatus: () => true,
      });
      if (res.status !== 204) logout();
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{
        adminKey,
        isAuthenticated: !!adminKey,
        login,
        logout,
        authedAxios,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);

export const API_BASE = API;
